<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Vault door rim at top -->
      <div class="vault-rim">
        <div class="vr-bolt" v-for="n in 8" :key="n"></div>
      </div>

      <!-- Header -->
      <header class="vault-header">
        <div class="vh-left">
          <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="vh-logo" />
          <div class="vh-secure">
            <span class="vs-icon">🔒</span>
            <span class="vs-text">SECURE SESSION</span>
          </div>
        </div>
        <div class="vh-dial" aria-hidden="true">
          <div class="dial-outer">
            <div class="dial-inner">
              <div class="dial-mark" v-for="n in 12" :key="n" :style="`transform: rotate(${n*30}deg) translateY(-28px)`"></div>
            </div>
          </div>
        </div>
        <a class="vh-cta" :href="applyRightLink">{{ heroPrimaryText }}</a>
      </header>

      <!-- Main vault body -->
      <div class="vault-body">

        <!-- Left: Big heavy hero -->
        <section class="vault-hero">
          <div class="vh-number-bg" aria-hidden="true">$</div>
          <div class="vh-content">
            <div class="vh-stamp">
              <span>APPROVED FOR LENDING</span>
            </div>
            <h1 class="vault-h1" v-html="heroH1Html"></h1>
            <div class="vh-rule-group">
              <div class="vhr vhr-thick"></div>
              <div class="vhr vhr-thin"></div>
            </div>
            <p class="vault-p">{{ heroBody }}</p>
            <div class="vault-btns">
              <a class="vbtn-primary" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="vbtn-sec" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
            <div class="vault-tel">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="vt-link">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="vt-link">{{ loinfo.email }}</a>
            </div>
          </div>
        </section>

        <!-- Right: Stacked "deposit box" cards -->
        <aside class="vault-boxes">
          <div class="vbox-label">AGENT REGISTRY</div>

          <div class="vault-box lo-vbox">
            <div class="vbox-num">BOX 001</div>
            <div class="vbox-handle lo-handle"></div>
            <div class="vbox-content">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="vbox-img" />
              <div class="vbox-info">
                <div class="vbox-title">LOAN OFFICER</div>
                <div class="vbox-name">{{ loName }}</div>
                <div class="vbox-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="vbox-nmls">NMLS # {{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="vbox-footer">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="vbox-link lo-vlink">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="vbox-link lo-vlink">{{ loinfo.email }}</a>
              <a class="vbox-cta lo-vcta" :href="applyRightLink">{{ heroPrimaryText }}</a>
            </div>
          </div>

          <div class="vault-box re-vbox">
            <div class="vbox-num">BOX 002</div>
            <div class="vbox-handle re-handle"></div>
            <div class="vbox-content">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="vbox-img" />
              <div class="vbox-info">
                <div class="vbox-title re-title">PARTNER</div>
                <div class="vbox-name">{{ realtorName }}</div>
                <div class="vbox-role">{{ realtor && realtor.brokerage_name }}</div>
              </div>
            </div>
            <div class="vbox-footer">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="vbox-link re-vlink">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="vbox-link re-vlink">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="vbox-link re-vlink" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>

        </aside>
      </div>

      <!-- Bottom vault rim -->
      <div class="vault-floor">
        <span class="vf-text">{{ loName }} · {{ loTitle }} · NMLS {{ loinfo && loinfo.nmls }}</span>
        <div class="vf-bolts">
          <div class="vfb" v-for="n in 4" :key="n"></div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandVault",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Your future,<br/><em>secured.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Roboto+Slab:wght@300;400;700&family=Roboto+Mono:wght@400;500&display=swap');

.page {
  min-height: 100vh;
  background: #1a1a18;
  color: #e0ddd6;
  font-family: 'Roboto Slab', serif;
  --p: #0b1726;
  --a: #c69c39;
  --steel: #2e2e2c;
  --border: rgba(224,221,214,0.12);
  --muted: #8a8880;
  --gold: #b8960a;
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* VAULT RIM */
.vault-rim {
  height: 20px;
  background: linear-gradient(180deg, #3a3a36 0%, #2a2a28 100%);
  border-bottom: 3px solid var(--gold);
  display: flex; align-items: center; justify-content: space-around; padding: 0 40px;
}
.vr-bolt {
  width: 12px; height: 12px; border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #6a6a60, #2a2a28);
  border: 1px solid #4a4a44;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.6);
}

/* HEADER */
.vault-header {
  background: linear-gradient(180deg, #252523 0%, #1e1e1c 100%);
  border-bottom: 2px solid var(--border);
  display: flex; align-items: center; justify-content: space-between; padding: 16px 48px;
}
.vh-left { display: flex; align-items: center; gap: 24px; }
.vh-logo { width: 140px; max-height: 72px; object-fit: contain; filter: brightness(0) invert(0.8) sepia(0.3); }
.vh-secure { display: flex; align-items: center; gap: 6px; background: rgba(184,150,10,0.1); border: 1px solid rgba(184,150,10,0.25); padding: 5px 12px; border-radius: 3px; }
.vs-icon { font-size: 11px; }
.vs-text { font-family: 'Roboto Mono', monospace; font-size: 9px; letter-spacing: 0.2em; color: var(--gold); }

/* DIAL */
.vh-dial { }
.dial-outer { width: 48px; height: 48px; border-radius: 50%; border: 3px solid #3a3a36; background: radial-gradient(circle at 40% 35%, #3e3e3a, #1e1e1c); display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.5); }
.dial-inner { width: 36px; height: 36px; border-radius: 50%; border: 1px solid #4a4a44; position: relative; display: flex; align-items: center; justify-content: center; animation: spin 20s linear infinite; }
.dial-mark { position: absolute; width: 2px; height: 6px; background: var(--gold); border-radius: 1px; transform-origin: center 28px; opacity: 0.6; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.vh-cta { font-family: 'Oswald', sans-serif; font-size: 12px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; text-decoration: none; background: var(--gold); color: #1a1a18; padding: 11px 24px; border-radius: 2px; transition: opacity 0.2s; }
.vh-cta:hover { opacity: 0.88; }

/* BODY */
.vault-body { display: grid; grid-template-columns: 1fr 380px; flex: 1; max-width: 1200px; margin: 0 auto; width: 100%; padding: 0; }

/* HERO */
.vault-hero { padding: 60px 52px 60px 48px; position: relative; overflow: hidden; border-right: 2px solid var(--border); }
.vh-number-bg { position: absolute; top: -20px; right: -20px; font-family: 'Oswald', sans-serif; font-size: 280px; font-weight: 700; color: rgba(184,150,10,0.04); line-height: 1; pointer-events: none; user-select: none; }
.vh-content { position: relative; z-index: 1; }
.vh-stamp {
  display: inline-block; border: 2px solid var(--gold); padding: 5px 14px; border-radius: 2px;
  font-family: 'Oswald', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 0.25em; color: var(--gold);
  transform: rotate(-2deg); margin-bottom: 28px; opacity: 0.7;
}
.vault-h1 { font-family: 'Oswald', sans-serif; font-size: clamp(44px, 6vw, 80px); font-weight: 700; line-height: 0.95; letter-spacing: 0.01em; color: #e8e5de; text-transform: uppercase; margin: 0 0 20px; }
.vault-h1 em { color: var(--gold); font-style: normal; }
.vh-rule-group { display: flex; flex-direction: column; gap: 3px; margin-bottom: 22px; }
.vhr { width: 60px; }
.vhr-thick { height: 3px; background: var(--gold); }
.vhr-thin { height: 1px; background: rgba(184,150,10,0.4); }
.vault-p { font-size: 14px; line-height: 1.76; color: var(--muted); margin-bottom: 32px; font-weight: 300; }
.vault-btns { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; justify-content: center;}
.vbtn-primary { font-family: 'Oswald', sans-serif; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; background: var(--gold); color: #1a1a18; padding: 13px 28px; border-radius: 2px; transition: opacity 0.2s; width: 55%; text-align: center; }
.vbtn-primary:hover { opacity: 0.88; }
.vbtn-sec { font-family: 'Oswald', sans-serif; font-size: 13px; font-weight: 400; text-decoration: none; color: var(--muted); border: 1px solid var(--border); padding: 13px 22px; border-radius: 2px; letter-spacing: 0.06em; transition: border-color 0.18s; }
.vbtn-sec:hover { border-color: var(--gold); color: var(--gold); }
.vault-tel { display: flex; flex-direction: column; gap: 4px; }
.vt-link { font-family: 'Roboto Mono', monospace; font-size: 11px; text-decoration: none; color: var(--muted); transition: color 0.18s; }
.vt-link:hover { color: var(--gold); }

/* VAULT BOXES */
.vault-boxes { padding: 40px 32px; display: flex; flex-direction: column; gap: 14px; }
.vbox-label { font-family: 'Roboto Mono', monospace; font-size: 9px; letter-spacing: 0.22em; color: var(--muted); margin-bottom: 4px; opacity: 0.6; }
.vault-box {
  background: linear-gradient(160deg, #282826 0%, #202020 100%);
  border: 1px solid var(--border);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.04), 0 4px 16px rgba(0,0,0,0.3);
}
.vbox-num { font-family: 'Roboto Mono', monospace; font-size: 8px; color: var(--muted); letter-spacing: 0.16em; padding: 8px 14px 0; opacity: 0.5; }
.vbox-handle { position: absolute; right: 14px; top: 14px; width: 20px; height: 20px; border-radius: 50%; border: 2px solid; }
.lo-handle { border-color: rgba(184,150,10,0.4); background: radial-gradient(circle at 40% 35%, #3a3830, #1a1a18); }
.re-handle { border-color: color-mix(in srgb, var(--a) 30%, transparent); background: radial-gradient(circle at 40% 35%, #302e28, #1a1a18); }
.vbox-content { display: flex; gap: 12px; align-items: flex-start; padding: 10px 14px 12px; }
.vbox-img { width: 48px; height: 48px; border-radius: 3px; object-fit: cover; flex-shrink: 0; border: 1px solid var(--border); filter: grayscale(20%); }
.vbox-title { font-family: 'Roboto Mono', monospace; font-size: 8px; letter-spacing: 0.18em; color: var(--gold); margin-bottom: 4px; opacity: 0.8; }
.re-title { color: var(--a); }
.vbox-name { font-family: 'Oswald', sans-serif; font-size: 16px; font-weight: 600; color: #e0ddd6; letter-spacing: 0.04em; }
.vbox-role { font-size: 11px; color: var(--muted); margin-top: 1px; font-family: 'Roboto Mono', monospace; font-size: 9px; }
.vbox-nmls { font-family: 'Roboto Mono', monospace; font-size: 9px; color: rgba(138,136,128,0.5); margin-top: 2px; }
.vbox-footer { padding: 8px 14px 14px; border-top: 1px solid var(--border); display: flex; flex-direction: column; gap: 4px; }
.vbox-link { font-family: 'Roboto Mono', monospace; font-size: 9px; text-decoration: none; color: var(--muted); transition: color 0.18s; }
.lo-vlink:hover { color: var(--gold); }
.re-vlink:hover { color: var(--a); }
.vbox-cta { display: block; margin-top: 8px; text-align: center; text-decoration: none; font-family: 'Oswald', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; padding: 9px; border-radius: 2px; background: var(--gold); color: #1a1a18; transition: opacity 0.2s; }
.vbox-cta:hover { opacity: 0.88; }

/* FLOOR */
.vault-floor {
  background: linear-gradient(180deg, #1e1e1c 0%, #161614 100%);
  border-top: 2px solid var(--border);
  border-top-color: var(--gold);
  display: flex; align-items: center; justify-content: space-between; padding: 12px 48px;
}
.vf-text { font-family: 'Roboto Mono', monospace; font-size: 9px; color: var(--muted); letter-spacing: 0.1em; opacity: 0.5; }
.vf-bolts { display: flex; gap: 12px; }
.vfb { width: 8px; height: 8px; border-radius: 50%; background: radial-gradient(circle at 35% 35%, #4a4a44, #2a2a28); border: 1px solid #3a3a36; }

@media (max-width: 820px) {
  .vault-body { grid-template-columns: 1fr; }
  .vault-hero { border-right: none; border-bottom: 2px solid var(--border); padding: 40px 24px; }
  .vault-boxes { padding: 24px; }
  .vault-header { padding: 14px 24px; }
  .vh-dial { display: none; }
  .vault-floor { padding: 12px 24px; flex-wrap: wrap; gap: 8px; }
}
</style>
